/* global Element */

import React, { useRef, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import { classNames } from '@zambezi/sdk/common'
import { scrollIntoViewIfNeeded } from './utils'

/**
 * Create a React Dropdown List Item component
 *
 * @template Item
 * @param {import('./interfaces').DropdownListItemProps<Item>} props
 * @returns {JSX.Element}
 */
function DropdownListItem ({
  id,
  className,
  itemProps,
  isFocused,
  isDisabled,
  index,
  data,
  onMouseOut,
  onMouseOver,
  onClick,
  children,
  listElementRef,
  style
}) {
  /** @type {import('react').RefObject<HTMLElement>} */
  const elementRef = useRef(null)
  useEffect(onFocusChanged, [
    elementRef.current,
    isFocused,
    listElementRef.current
  ])

  /** @type {import('./interfaces').ItemMouseEventHandler<Item>} */
  const handleItemClick = useCallback(event => onClick(data, index, event), [
    onClick,
    data,
    index,
    isDisabled
  ])
  /** @type {import('./interfaces').ItemMouseEventHandler<Item>} */
  const handleItemMouseOver = useCallback(
    event => onMouseOver(data, index, event),
    [onMouseOver, index]
  )

  const disabledHandler = isDisabled ? null : true

  return (
    <div
      id={id}
      role='option'
      aria-disabled={isDisabled ? 'true' : undefined}
      className={classNames(
        'zb-dropdown-list-item',
        {
          'zb-dropdown-list-item-is-focused': isFocused,
          'zb-dropdown-list-item-is-disabled': isDisabled
        },
        className
      )}
      {...itemProps}
      onClick={disabledHandler && handleItemClick}
      onMouseEnter={disabledHandler && handleItemMouseOver}
      onMouseLeave={disabledHandler && onMouseOut}
      ref={elementRef}
      style={style || null}
    >
      {children}
    </div>
  )

  function onFocusChanged () {
    const element = elementRef.current
    const listElement = listElementRef.current
    if (element && isFocused && listElement) {
      scrollIntoViewIfNeeded(element, listElement)
    }
  }
}

DropdownListItem.propTypes = {
  listElementRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) })
    .isRequired,
  className: PropTypes.string,
  itemProps: PropTypes.object,
  isFocused: PropTypes.bool,
  isDisabled: PropTypes.bool,
  id: PropTypes.string,
  index: PropTypes.number.isRequired,
  data: PropTypes.any,
  onMouseOut: PropTypes.func.isRequired,
  onMouseOver: PropTypes.func.isRequired,
  onClick: PropTypes.func.isRequired,
  style: PropTypes.object
}

export default DropdownListItem
